import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { DEFAULT_PIPELINE_STAGES, SUPPORTED_CURRENCIES } from '@trinco/shared';
import type { Deal } from '@trinco/shared';
import { trpc } from '@/trpc';
import { useAuth } from '@/hooks/use-auth';

interface DealFormProps {
  deal?: Deal | null;
  onSave: (deal?: any) => void;
  onCancel: () => void;
}

export function DealForm({ deal, onSave, onCancel }: DealFormProps) {
  const { user } = useAuth();
  const userRole = (user as any)?.role;
  const canSetPortco = userRole === 'admin' || userRole === 'director';

  const [title, setTitle] = useState(deal?.title ?? '');
  const [companyName, setCompanyName] = useState(deal?.companyName ?? '');
  const [contactName, setContactName] = useState(deal?.contactName ?? '');
  const [contactEmail, setContactEmail] = useState(deal?.contactEmail ?? '');
  const [amount, setAmount] = useState(deal?.amount?.toString() ?? '');
  const [currency, setCurrency] = useState(deal?.currency ?? 'CAD');
  const [stage, setStage] = useState(deal?.stage ?? 'lead');
  const [expectedClose, setExpectedClose] = useState(deal?.expectedClose ?? '');
  const [sourcedBy, setSourcedBy] = useState(deal?.sourcedBy ?? '');
  const [portcoId, setPortcoId] = useState(deal?.portcoId ?? '');
  const [notes, setNotes] = useState(deal?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: teamMembers } = (trpc as any).team?.list?.useQuery?.() ?? { data: [] };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);

    try {
      // Will be wired to trpc.deal.create or trpc.deal.update
      const payload = {
        title: title.trim(),
        companyName: companyName.trim() || null,
        contactName: contactName.trim() || null,
        contactEmail: contactEmail.trim() || null,
        amount: amount ? parseFloat(amount) : null,
        currency,
        stage,
        expectedClose: expectedClose || null,
        sourcedBy: sourcedBy || null,
        portcoId: portcoId.trim() || null,
        notes: notes.trim() || null,
      };

      // Placeholder: simulate API call
      await new Promise((r) => setTimeout(r, 300));
      onSave({ id: deal?.id ?? 'new', ...payload });
    } catch {
      setError('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="title">Deal title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Acme Corp - Q2 Contract"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Company</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName">Contact name</Label>
              <Input
                id="contactName"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Primary contact"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactEmail">Contact email</Label>
              <Input
                id="contactEmail"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stage">Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_PIPELINE_STAGES.map((s) => (
                    <SelectItem key={s.slug} value={s.slug}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedClose">Expected close date</Label>
              <Input
                id="expectedClose"
                type="date"
                value={expectedClose}
                onChange={(e) => setExpectedClose(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sourcedBy">Sourced by</Label>
              <Select value={sourcedBy} onValueChange={setSourcedBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">— None —</SelectItem>
                  {(teamMembers ?? []).map((m: any) => (
                    <SelectItem key={m.userId} value={m.userId}>
                      {m.user?.name ?? m.userId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canSetPortco && (
              <div className="space-y-2">
                <Label htmlFor="portcoId">Portco</Label>
                <Input
                  id="portcoId"
                  value={portcoId}
                  onChange={(e) => setPortcoId(e.target.value)}
                  placeholder="e.g. acme (portco slug)"
                />
              </div>
            )}

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : deal ? 'Update deal' : 'Create deal'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
